"use client";

import Image from "next/image";
import React, { useState } from "react";

import { Text, TextSize, TextWeight } from "../../components";
import type { OrderProduct } from "../../types";

// Skeleton komponent dla ładowania
export const ReturnedProductListItemSkeleton = () => (
  <div className="p-4 bg-white border border-gray-100 animate-pulse">
    {/* Mobile layout */}
    <div className="flex flex-col sm:hidden">
      <div className="w-full h-[200px] bg-gray-200 mb-4 rounded"></div>

      <div className="flex flex-col gap-4">
        <div className="flex-1 flex flex-col gap-1">
          {/* Product name */}
          <div className="h-5 bg-gray-200 rounded w-3/4"></div>

          {/* Size */}
          <div className="h-4 bg-gray-200 rounded w-1/3"></div>

          {/* Color */}
          <div className="h-4 bg-gray-200 rounded w-1/4"></div>

          {/* Reference */}
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>

          {/* Return reason */}
          <div className="h-4 bg-gray-200 rounded w-2/3 mt-2"></div>
        </div>

        <div className="flex justify-between items-end">
          <div className="text-right flex-1">
            <div className="h-5 bg-gray-200 rounded w-32 mb-1"></div>
            <div className="h-4 bg-gray-200 rounded w-8"></div>
          </div>
        </div>
      </div>
    </div>

    {/* Desktop layout */}
    <div className="hidden sm:flex items-start gap-4">
      {/* Image placeholder */}
      <div className="w-[90px] h-[135px] bg-gray-200 flex-shrink-0 rounded"></div>

      <div className="flex-1 flex flex-col gap-1">
        {/* Product name */}
        <div className="h-5 bg-gray-200 rounded w-3/4"></div>

        {/* Size */}
        <div className="h-4 bg-gray-200 rounded w-1/3"></div>

        {/* Color */}
        <div className="h-4 bg-gray-200 rounded w-1/4"></div>

        {/* Reference */}
        <div className="h-4 bg-gray-200 rounded w-1/2"></div>

        {/* Return reason */}
        <div className="h-4 bg-gray-200 rounded w-2/3 mt-2"></div>
      </div>

      <div className="flex flex-col items-end gap-4">
        <div className="text-right flex gap-2">
          <div className="h-5 bg-gray-200 rounded w-24"></div>
          <div className="h-5 bg-gray-200 rounded w-8"></div>
        </div>
      </div>
    </div>
  </div>
);

interface ReturnedProductListItemProps {
  product: OrderProduct & {
    color?: string;
    size?: string;
    returnReason?: string;
  };
}

// Komponent dla pojedynczego produktu
export const ReturnedProductListItem = ({
  product,
}: ReturnedProductListItemProps) => {
  const [imageError, setImageError] = useState<boolean>(false);

  // Funkcja do generowania URL obrazka produktu
  const getProductImageUrl = (product: any) => {
    // Sprawdź czy produkt ma obraz w strukturze image lub cover
    const imageId = product.image?.idImage || product.cover?.idImage;
    const productName = product.name || product.productName;
    const domain = "https://carry.pl";

    if (imageId && productName) {
      // Nowy format URL: /imageId-cart_default/product-name-slug.jpg
      // Konwertuj nazwę produktu na slug
      const slug = productName
        .toLowerCase()
        .replace(/ą/g, "a")
        .replace(/ć/g, "c")
        .replace(/ę/g, "e")
        .replace(/ł/g, "l")
        .replace(/ń/g, "n")
        .replace(/ó/g, "o")
        .replace(/ś/g, "s")
        .replace(/ź/g, "z")
        .replace(/ż/g, "z")
        .replace(/[^a-z0-9\s-]/g, "")
        .replace(/[\s_]+/g, "-")
        .replace(/-+/g, "-")
        .trim("-");

      return `${domain}/${imageId}-cart_default/${slug}.jpg`;
    }

    if (product.imageUrl) {
      return product.imageUrl
        .replace("http://localhost/prestashop", "https://shop.carry.pl")
        .replace("carry-prestashop.ulam.tech", "shop.carry.pl");
    }

    // Fallback - jeśli nie ma wystarczających danych
    if (product.idProduct) {
      return `${domain}/${product.idProduct}-cart_default.jpg`;
    }

    return null;
  };

  const imageUrl = getProductImageUrl(product);

  return (
    <div className="p-4 bg-white border border-gray-100 relative">
      <div className="flex flex-col sm:hidden">
        <div className="w-full h-[200px] bg-gray-100 flex items-center justify-center mb-4">
          {imageUrl && !imageError ? (
            <Image
              src={imageUrl}
              alt={product.name || "Produkt"}
              width={200}
              height={200}
              className="object-cover max-h-full max-w-full"
              onError={() => setImageError(true)}
              placeholder="blur"
              blurDataURL="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAAIAAoDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAhEAACAQMDBQAAAAAAAAAAAAABAgMABAUGIWGRkqGx0f/EABUBAQEAAAAAAAAAAAAAAAAAAAMF/8QAGhEAAgIDAAAAAAAAAAAAAAAAAAECEgMRkf/aAAwDAQACEQMRAD8AltJagyeH0AthI5xdrLcNM91BF5pX2HaH9bcfaSXWGaRmknyJckliyjqTzSlT54b6bk+h0R//2Q=="
            />
          ) : (
            <span className="text-gray-400 text-lg text-center">IMG</span>
          )}
        </div>

        <div className="flex flex-col gap-4">
          <div className="flex-1 flex flex-col gap-1">
            <Text
              text={product.name}
              fontSize={TextSize.M}
              weight={TextWeight.MEDIUM}
            />
            {product.size && (
              <Text text={`Rozmiar: ${product.size}`} fontSize={TextSize.S} />
            )}
            {product.color && (
              <Text text={`Kolor: ${product.color}`} fontSize={TextSize.S} />
            )}
            <Text text={`Indeks: ${product.reference}`} fontSize={TextSize.S} />

            {product.returnReason && (
              <Text
                text={`Powód zwrotu: ${product.returnReason}`}
                fontSize={TextSize.S}
                className="mt-2"
              />
            )}
          </div>

          <div className="flex justify-between items-end">
            <div className="text-right flex-1">
              <Text text="Zwrócona ilość:" />
              <Text
                text={product.productQuantity}
                fontSize={TextSize.L}
                weight={TextWeight.BOLD}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Desktop layout - original horizontal layout */}
      <div className="hidden sm:flex items-start gap-4">
        {/* Placeholder dla obrazka produktu */}
        <div className="w-[90px] h-[135px] bg-gray-100 flex-shrink-0 flex items-center justify-center">
          {imageUrl && !imageError ? (
            <Image
              src={imageUrl}
              alt={product.name || "Produkt"}
              width={90}
              height={135}
              className="object-cover"
              onError={() => setImageError(true)}
              placeholder="blur"
              blurDataURL="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAAIAAoDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAhEAACAQMDBQAAAAAAAAAAAAABAgMABAUGIWGRkqGx0f/EABUBAQEAAAAAAAAAAAAAAAAAAAMF/8QAGhEAAgIDAAAAAAAAAAAAAAAAAAECEgMRkf/aAAwDAQACEQMRAD8AltJagyeH0AthI5xdrLcNM91BF5pX2HaH9bcfaSXWGaRmknyJckliyjqTzSlT54b6bk+h0R//2Q=="
            />
          ) : (
            <span className="text-gray-400 text-xs text-center">IMG</span>
          )}
        </div>

        <div className="flex-1 flex flex-col gap-1">
          <Text
            text={product.name}
            fontSize={TextSize.M}
            weight={TextWeight.MEDIUM}
          />
          {product.size && (
            <Text text={`Rozmiar: ${product.size}`} fontSize={TextSize.S} />
          )}
          {product.color && (
            <Text text={`Kolor: ${product.color}`} fontSize={TextSize.S} />
          )}
          <Text text={`Indeks: ${product.reference}`} fontSize={TextSize.S} />

          {product.returnReason && (
            <Text
              text={`Powód zwrotu: ${product.returnReason}`}
              fontSize={TextSize.S}
              className="mt-2"
            />
          )}
        </div>

        <div className="flex flex-col items-end gap-4">
          <div className="text-right flex gap-2">
            <Text text="Zwrócona ilość:" fontSize={TextSize.L} />
            <Text
              text={product.productQuantity}
              fontSize={TextSize.L}
              weight={TextWeight.BOLD}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
